/**
 * VK1 Architecture Visualization
 * 
 * Visual representation of VK1 layers and their relationships
 * Demonstrates the foundational architecture for cross-system semantic authority federation
 */

import React from 'react';

interface Layer {
  id: string;
  name: string;
  description: string;
  color: string;
  position: { x: number; y: number };
  connections: string[];
}

const layers: Layer[] = [
  {
    id: 'core',
    name: 'VK1 Core',
    description: 'Execution truth - immutable, never shared',
    color: 'bg-red-600',
    position: { x: 50, y: 10 },
    connections: ['receipts']
  },
  {
    id: 'receipts',
    name: 'Receipts',
    description: 'Immutable evidence stream with timestamps',
    color: 'bg-orange-600',
    position: { x: 50, y: 25 },
    connections: ['sig', 'ctar', 'probes', 'ledger']
  },
  {
    id: 'sig',
    name: 'SIG',
    description: 'Structural Identity Graph - identity continuity',
    color: 'bg-blue-600',
    position: { x: 25, y: 45 },
    connections: ['arbitration']
  },
  {
    id: 'ctar',
    name: 'CTAR',
    description: 'Canonical Terminology & Authority Registry - semantic authority',
    color: 'bg-purple-600',
    position: { x: 50, y: 45 },
    connections: ['arbitration']
  },
  {
    id: 'probes',
    name: 'Probes',
    description: 'Constitutional Observability Layer - drift detection',
    color: 'bg-green-600',
    position: { x: 75, y: 45 },
    connections: ['arbitration']
  },
  {
    id: 'ledger',
    name: 'Ledger',
    description: 'Architecture Ledger - system state snapshots',
    color: 'bg-yellow-600',
    position: { x: 50, y: 60 },
    connections: ['arbitration']
  },
  {
    id: 'arbitration',
    name: 'Arbitration',
    description: 'Cross-Layer Arbitration - disagreement representation',
    color: 'bg-pink-600',
    position: { x: 50, y: 75 },
    connections: ['temporal']
  },
  {
    id: 'temporal',
    name: 'Temporal Consistency',
    description: 'Distributed Temporal Consistency - time without global clocks',
    color: 'bg-cyan-600',
    position: { x: 50, y: 90 },
    connections: []
  }
];

export const VK1ArchitectureVisualization: React.FC = () => {
  const [selectedLayer, setSelectedLayer] = React.useState<Layer | null>(null);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">VK1 Architecture</h1>
          <p className="text-gray-400">Foundational architecture for cross-system semantic authority federation</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Architecture Diagram */}
          <div className="bg-[#1a1a2e] rounded-lg p-6 relative" style={{ minHeight: '600px' }}>
            <h2 className="text-xl font-semibold mb-4">Layer Architecture</h2>
            
            {layers.map((layer) => (
              <div
                key={layer.id}
                className={`absolute cursor-pointer transition-all duration-200 ${
                  selectedLayer?.id === layer.id ? 'scale-110 z-10' : 'hover:scale-105'
                }`}
                style={{
                  left: `${layer.position.x}%`,
                  top: `${layer.position.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
                onClick={() => setSelectedLayer(layer)}
              >
                <div className={`${layer.color} px-4 py-2 rounded-lg shadow-lg text-center min-w-32`}>
                  <div className="font-semibold text-sm">{layer.name}</div>
                </div>
              </div>
            ))}

            {/* Connection Lines (simplified) */}
            <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
              {layers.flatMap(layer =>
                layer.connections.map(targetId => {
                  const target = layers.find(l => l.id === targetId);
                  if (!target) return null;
                  return (
                    <line
                      key={`${layer.id}-${targetId}`}
                      x1={`${layer.position.x}%`}
                      y1={`${layer.position.y}%`}
                      x2={`${target.position.x}%`}
                      y2={`${target.position.y}%`}
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                  );
                })
              )}
            </svg>
          </div>

          {/* Layer Details */}
          <div className="bg-[#1a1a2e] rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Layer Details</h2>
            
            {selectedLayer ? (
              <div className="space-y-4">
                <div className={`${selectedLayer.color} px-4 py-2 rounded-lg inline-block`}>
                  <h3 className="text-2xl font-bold">{selectedLayer.name}</h3>
                </div>
                <p className="text-gray-300 text-lg">{selectedLayer.description}</p>
                
                <div className="mt-6">
                  <h4 className="font-semibold mb-2">Key Principles</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    {selectedLayer.id === 'core' && (
                      <>
                        <li>Execution truth is local and immutable</li>
                        <li>Never shared across systems</li>
                        <li>Fail-closed enforcement</li>
                      </>
                    )}
                    {selectedLayer.id === 'receipts' && (
                      <>
                        <li>Immutable evidence stream</li>
                        <li>Receipt-derived only</li>
                        <li>Timestamps with uncertainty</li>
                      </>
                    )}
                    {selectedLayer.id === 'sig' && (
                      <>
                        <li>Identity continuity over time</li>
                        <li>Receipt-derived identity resolution</li>
                        <li>Explicit uncertainty representation</li>
                      </>
                    )}
                    {selectedLayer.id === 'ctar' && (
                      <>
                        <li>Semantic authority registry</li>
                        <li>Explicit binding interfaces</li>
                        <li>Drift detection signals</li>
                      </>
                    )}
                    {selectedLayer.id === 'probes' && (
                      <>
                        <li>Coherence evaluation</li>
                        <li>Drift over time detection</li>
                        <li>Advisory-only signals</li>
                      </>
                    )}
                    {selectedLayer.id === 'ledger' && (
                      <>
                        <li>System state snapshots</li>
                        <li>Multi-timeline support</li>
                        <li>State evolution tracking</li>
                      </>
                    )}
                    {selectedLayer.id === 'arbitration' && (
                      <>
                        <li>Disagreement representation</li>
                        <li>Non-authoritative classification</li>
                        <li>Multi-valid interpretation preservation</li>
                      </>
                    )}
                    {selectedLayer.id === 'temporal' && (
                      <>
                        <li>Time is not global truth</li>
                        <li>Partial ordering support</li>
                        <li>Explicit temporal uncertainty</li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="mt-6">
                  <h4 className="font-semibold mb-2">Connections</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedLayer.connections.map(connId => {
                      const connLayer = layers.find(l => l.id === connId);
                      return connLayer ? (
                        <span key={connId} className={`${connLayer.color} px-3 py-1 rounded text-sm`}>
                          {connLayer.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-400 text-center py-12">
                <p>Click on a layer to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Key Invariants */}
        <div className="mt-8 bg-[#1a1a2e] rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Key Architectural Invariants</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-[#0a0a0f] p-4 rounded-lg">
              <h3 className="font-semibold text-red-400 mb-2">No Global Truth</h3>
              <p className="text-gray-400 text-sm">Time, semantics, and authority are distributed - no centralized coordination required</p>
            </div>
            <div className="bg-[#0a0a0f] p-4 rounded-lg">
              <h3 className="font-semibold text-blue-400 mb-2">Receipt-Derived</h3>
              <p className="text-gray-400 text-sm">All layers derive from immutable execution evidence, never from inference</p>
            </div>
            <div className="bg-[#0a0a0f] p-4 rounded-lg">
              <h3 className="font-semibold text-green-400 mb-2">Advisory-Only</h3>
              <p className="text-gray-400 text-sm">SIG, CTAR, Probes, Arbitration are advisory - never influence execution</p>
            </div>
            <div className="bg-[#0a0a0f] p-4 rounded-lg">
              <h3 className="font-semibold text-purple-400 mb-2">Represent, Don't Resolve</h3>
              <p className="text-gray-400 text-sm">Disagreement is recorded and classified, not automatically corrected</p>
            </div>
            <div className="bg-[#0a0a0f] p-4 rounded-lg">
              <h3 className="font-semibold text-yellow-400 mb-2">Fail-Closed</h3>
              <p className="text-gray-400 text-sm">Missing authority or unresolved governance blocks side effects</p>
            </div>
            <div className="bg-[#0a0a0f] p-4 rounded-lg">
              <h3 className="font-semibold text-cyan-400 mb-2">Explicit Uncertainty</h3>
              <p className="text-gray-400 text-sm">Uncertainty is represented and propagated, never hidden or assumed away</p>
            </div>
          </div>
        </div>

        {/* Federation Note */}
        <div className="mt-8 bg-gradient-to-r from-purple-900 to-blue-900 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-2">Cross-System Federation</h2>
          <p className="text-gray-300">
            This architecture enables cross-system semantic authority federation by federating <strong>references</strong> to semantic authority 
            (CTAR bindings, identity mappings, disagreement states) without requiring shared meaning or centralized control. 
            Each system maintains local autonomy while coordinating through explicit, receipt-derived interfaces.
          </p>
          <p className="text-gray-400 mt-2 text-sm">
            <em>Coordination without agreement — the core invariant for distributed AI governance.</em>
          </p>
        </div>
      </div>
    </div>
  );
};
